import React, { useEffect, useRef, useState } from "react";
import "./HeroShowcase.css";
import {
    LiveSignalEmblem,
    GeofenceEmblem,
    SchedulingEmblem,
    PayrollEmblem,
    DocumentHubEmblem,
    ApplicantTrackingEmblem,
    SelfServiceEmblem,
    ClientCareEmblem,
} from "./Emblems";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * HeroShowcase
 *
 * Animation is now driven by scroll progress, not a fixed-duration timer.
 * The section is held at its hidden state until it starts entering the
 * viewport from the bottom, and reaches its fully-revealed state exactly
 * when the section has completely scrolled into view. Scrolling back up
 * reverses it the same way, since progress is recalculated every frame.
 *
 * SWAP BEFORE SHIPPING:
 *  - Replace the placeholder headline text in .major-text / .gradient-text
 *  - Replace .hero-subject-block divs with real imagery/components
 *  - Replace .hero-caption placeholder text with real captions
 */
const HeroShowcase = ({ id }) => {
    const sectionRef = useRef(null);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        if (prefersReducedMotion) {
            setProgress(1);
            return;
        }

        let frame = null;

        const updateProgress = () => {
            const el = sectionRef.current;
            if (!el) return;

            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight;

            // 0 when the section's top edge is still at the bottom of the
            // viewport (just starting to enter). 1 once the section's full
            // height has scrolled past that point (fully entered view).
            const next = clamp((vh - rect.top) / rect.height, 0, 1);
            setProgress(next);
        };

        const onScrollOrResize = () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
                updateProgress();
                frame = null;
            });
        };

        updateProgress();
        window.addEventListener("scroll", onScrollOrResize, { passive: true });
        window.addEventListener("resize", onScrollOrResize);

        return () => {
            window.removeEventListener("scroll", onScrollOrResize);
            window.removeEventListener("resize", onScrollOrResize);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    // Stagger the two headline spans within the same scroll window, rather
    // than with a time-based delay, by mapping each to its own sub-range
    // of the overall progress value.
    const majorProgress = clamp(progress * 1.4, 0, 1);
        const gradientProgress = clamp((progress - 0.15) * 1.4, 0, 1);

        const isMobile = typeof window !== "undefined" && window.innerWidth <= 734;
        const row1Base = isMobile ? -60 : -160;
        const row2Base = isMobile ? 60 : 120;
        const row1Offset = row1Base * (1 - progress);
        const row2Offset = row2Base * (1 - progress);

            const row1Captions = [
                "Live Activity Monitoring",
                "Geofenced Clock-In",
                "Shift Scheduling",
                "Payroll Automation",
            ];
            const row2Captions = [
                "Document Hub",
                "Applicant Tracking",
                "Employee Self-Service",
                "Client & Care Management",
            ];

            return (
            <section id={id} className="hero-showcase" ref={sectionRef}>
                <div className="hero-showcase-headline">
                    <h1 className="hero-headline">
                        <span
                            className="major-text"
                            style={{
                                opacity: majorProgress,
                                transform: `translateY(${25 * (1 - majorProgress)}px)`,
                            }}
                        >
                            Clock-ins, locations, and hours.&nbsp;
                        </span>
                        <span
                            className="gradient-text"
                            style={{
                                opacity: gradientProgress,
                                transform: `translateY(${25 * (1 - gradientProgress)}px)`,
                            }}
                        >
                            All-in-One.
                        </span>
                    </h1>
                </div>

                <div className="hero-subjects" role="presentation" aria-hidden="true">
                    <div
                        className="hero-subject-row hero-subject-row-1"
                        style={{
                            transform: `translateX(${row1Offset}px)`,
                            opacity: progress,
                        }}
                    >
                        {[1, 2, 3, 4].map((i) => (
                            <div className="hero-subject" key={`row1-${i}`}>
                                <div
                                    className={`hero-subject-block${
                                        i === 2 ? " emblem-glass-tile" : ""
                                    }`}
                                >
                                    {i === 1 && <LiveSignalEmblem />}
                                    {i === 2 && <GeofenceEmblem />}
                                    {i === 3 && <SchedulingEmblem />}
                                    {i === 4 && <PayrollEmblem />}
                                </div>
                                <p className="hero-caption">{row1Captions[i - 1]}</p>
                            </div>
                        ))}
                    </div>

                    <div
                        className="hero-subject-row hero-subject-row-2"
                        style={{
                            transform: `translateX(${row2Offset}px)`,
                            opacity: progress,
                        }}
                    >
                        {[1, 2, 3, 4].map((i) => (
                            <div className="hero-subject" key={`row2-${i}`}>
                                <div className="hero-subject-block">
                                    {i === 1 && <DocumentHubEmblem />}
                                    {i === 2 && <ApplicantTrackingEmblem />}
                                    {i === 3 && <SelfServiceEmblem />}
                                    {i === 4 && <ClientCareEmblem />}
                                </div>
                                <p className="hero-caption">{row2Captions[i - 1]}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    };

    export default HeroShowcase;